// The draft editor: upload a PDF, add recipients, place each signer's fields, send.
// Saves automatically (debounced); the server re-validates everything on save and again on send.
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, CalendarDays, CheckSquare, Copy, FileUp, Loader2, PenLine, Send, Signature, Trash2, Type, User, UserPlus, X } from "lucide-react";
import { useMe } from "../App";
import { api, ApiError } from "../lib/api";
import { DEFAULT_SIZES, FIELD_LABEL, bytes, newId, rColor } from "../lib/format";
import type { EnvelopeView, Field, FieldType, Recipient, RecordLink } from "../lib/types";
import { Card, inputCls } from "../components/bits";
import { PdfPages } from "../components/PdfPages";
import { RecordPicker } from "../components/Connectors";
import { Button, ConfirmDialog, cx, ErrorNote, Modal, toast } from "../components/ui";

const PALETTE: { type: FieldType; icon: typeof Signature }[] = [
  { type: "signature", icon: Signature },
  { type: "initials", icon: PenLine },
  { type: "dateSigned", icon: CalendarDays },
  { type: "name", icon: User },
  { type: "text", icon: Type },
  { type: "checkbox", icon: CheckSquare },
];

type Draft = { title: string; message: string; routing: "sequential" | "parallel"; expiryDays: number; reminderDays: number; recipients: Recipient[]; fields: Field[] };

export default function Editor({ initial, onSent }: { initial: EnvelopeView; onSent: (v: EnvelopeView) => void }) {
  const me = useMe();
  const nav = useNavigate();
  const env = initial.envelope;
  const [view, setView] = useState(initial);
  const [draft, setDraft] = useState<Draft>({
    title: env.title,
    message: env.message,
    routing: env.routing,
    expiryDays: env.expiryDays,
    reminderDays: env.reminderDays,
    recipients: initial.recipients,
    fields: initial.fields,
  });
  const [active, setActive] = useState<string | null>(initial.recipients.find((r) => r.role === "signer")?.rid ?? null);
  const [placing, setPlacing] = useState<FieldType | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"saved" | "dirty" | "saving" | "error">("saved");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ---------- autosave ----------
  const latest = useRef(draft);
  latest.current = draft;
  const saving = useRef<Promise<void> | null>(null);
  const timer = useRef<number | null>(null);
  const save = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    while (saving.current) await saving.current;
    const body = latest.current;
    setSaveState("saving");
    saving.current = (async () => {
      try {
        const v = await api<EnvelopeView>(`/envelopes/${env.id}`, { body });
        setView((old) => ({ ...old, problems: v.problems, envelope: v.envelope }));
        setSaveState(latest.current === body ? "saved" : "dirty");
        setSaveError(null);
      } catch (e) {
        setSaveState("error");
        setSaveError((e as Error).message);
        throw e;
      } finally {
        saving.current = null;
      }
    })();
    await saving.current;
  }, [env.id]);
  const update = (fn: (d: Draft) => Draft) => {
    setDraft((d) => fn(d));
    setSaveState("dirty");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save().catch(() => undefined), 800);
  };
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (saveState === "dirty" || saveState === "saving") e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saveState]);

  // ---------- recipients ----------
  const signers = draft.recipients.filter((r) => r.role === "signer");
  const nextColor = () => {
    const used = new Set(draft.recipients.map((r) => r.color));
    for (let i = 0; i < 10; i++) if (!used.has(i)) return i;
    return draft.recipients.length % 10;
  };
  const addRecipient = (meToo = false) => {
    if (draft.recipients.length >= me.limits.maxRecipients) return toast(`At most ${me.limits.maxRecipients} recipients`, "error");
    if (meToo && draft.recipients.some((r) => r.isSender)) return;
    const r: Recipient = { rid: newId("rcp"), name: meToo ? me.name : "", email: meToo ? me.email : "", role: "signer", order: Math.max(0, ...draft.recipients.map((x) => x.order)) + 1, status: "pending", color: nextColor(), ...(meToo ? { isSender: true } : {}) };
    update((d) => ({ ...d, recipients: [...d.recipients, r] }));
    setActive(r.rid);
  };
  const setRecipient = (rid: string, patch: Partial<Recipient>) => update((d) => ({ ...d, recipients: d.recipients.map((r) => (r.rid === rid ? { ...r, ...patch } : r)), fields: patch.role === "cc" ? d.fields.filter((f) => f.rid !== rid) : d.fields }));
  const removeRecipient = (rid: string) => {
    update((d) => ({ ...d, recipients: renumber(d.recipients.filter((r) => r.rid !== rid)), fields: d.fields.filter((f) => f.rid !== rid) }));
    if (active === rid) setActive(signers.find((r) => r.rid !== rid)?.rid ?? null);
  };
  const move = (rid: string, dir: -1 | 1) =>
    update((d) => {
      const list = [...d.recipients].sort((a, b) => a.order - b.order);
      const i = list.findIndex((r) => r.rid === rid);
      const j = i + dir;
      if (j < 0 || j >= list.length) return d;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...d, recipients: renumber(list) };
    });

  // ---------- fields ----------
  const pages = view.envelope.pages ?? [];
  const place = (page: number, fx: number, fy: number) => {
    if (!placing || !active) return;
    const pg = pages[page];
    const size = DEFAULT_SIZES[placing];
    const w = Math.min(1, size.w / pg.w);
    const h = Math.min(1, size.h / pg.h);
    const f: Field = { fid: newId("fld"), rid: active, type: placing, page, x: clamp(fx - w / 2, 0, 1 - w), y: clamp(fy - h / 2, 0, 1 - h), w, h, required: placing !== "checkbox" };
    if (draft.fields.filter((x) => x.rid === active).length >= me.limits.maxFieldsPerRecipient) return toast(`At most ${me.limits.maxFieldsPerRecipient} fields per signer`, "error");
    update((d) => ({ ...d, fields: [...d.fields, f] }));
    setSelected(f.fid);
    setPlacing(null);
  };
  const setField = (fid: string, patch: Partial<Field>) => update((d) => ({ ...d, fields: d.fields.map((f) => (f.fid === fid ? { ...f, ...patch } : f)) }));
  const removeField = (fid: string) => {
    update((d) => ({ ...d, fields: d.fields.filter((f) => f.fid !== fid) }));
    setSelected(null);
  };
  const copyToAllPages = (fid: string) => {
    const f = draft.fields.find((x) => x.fid === fid);
    if (!f) return;
    const copies = pages.map((_, i) => i).filter((i) => i !== f.page && !draft.fields.some((x) => x.rid === f.rid && x.type === f.type && x.page === i && Math.abs(x.x - f.x) < 0.01 && Math.abs(x.y - f.y) < 0.01)).map((i) => ({ ...f, fid: newId("fld"), page: i }));
    if (draft.fields.filter((x) => x.rid === f.rid).length + copies.length > me.limits.maxFieldsPerRecipient) return toast(`At most ${me.limits.maxFieldsPerRecipient} fields per signer`, "error");
    update((d) => ({ ...d, fields: [...d.fields, ...copies] }));
    toast(`Copied to ${copies.length} page${copies.length === 1 ? "" : "s"}`);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest("input, textarea, select")) return;
      if (e.key === "Escape") {
        setPlacing(null);
        setSelected(null);
      }
      if (selected && (e.key === "Delete" || e.key === "Backspace")) {
        e.preventDefault();
        removeField(selected);
      }
      if (selected && e.key.startsWith("Arrow")) {
        const f = draft.fields.find((x) => x.fid === selected);
        if (!f) return;
        e.preventDefault();
        const step = e.shiftKey ? 0.01 : 0.002;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        setField(selected, { x: clamp(f.x + dx, 0, 1 - f.w), y: clamp(f.y + dy, 0, 1 - f.h) });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // ---------- upload ----------
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = async (file: File) => {
    if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) return toast("Choose a PDF. Save Word documents as PDF first.", "error");
    if (file.size > me.limits.maxPdfBytes) return toast(`PDFs can be at most ${bytes(me.limits.maxPdfBytes)}`, "error");
    setUploading(file.name);
    try {
      await save().catch(() => undefined);
      const { url } = await api<{ url: string }>(`/envelopes/${env.id}/upload`, { body: { size: file.size } });
      const put = await fetch(url, { method: "PUT", body: file, headers: { "content-type": "application/pdf" } });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      const v = await api<EnvelopeView>(`/envelopes/${env.id}/document`, { body: { fileName: file.name } });
      setView(v);
      update((d) => ({ ...d, fields: v.fields, title: d.title === "Untitled" ? file.name.replace(/\.pdf$/i, "").slice(0, me.limits.maxTitle) : d.title }));
      if (!draft.recipients.length) addRecipient();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setUploading(null);
    }
  };

  const fieldsByPage = useMemo(() => {
    const m = new Map<number, Field[]>();
    for (const f of draft.fields) m.set(f.page, [...(m.get(f.page) ?? []), f]);
    return m;
  }, [draft.fields]);
  const byRid = useMemo(() => new Map(draft.recipients.map((r) => [r.rid, r])), [draft.recipients]);
  const sel = draft.fields.find((f) => f.fid === selected);
  const activeR = active ? byRid.get(active) : undefined;

  return (
    <div className="min-h-full flex flex-col">
      {/* top bar */}
      <div className="sticky top-0 z-20 bg-surface border-b border-line px-4 h-14 flex items-center gap-3">
        <Link to="/" className="p-2 -ml-2 rounded-lg text-muted hover:bg-surface-2" aria-label="Back to envelopes">
          <ArrowLeft className="size-4" />
        </Link>
        <input value={draft.title} onChange={(e) => update((d) => ({ ...d, title: e.target.value }))} maxLength={me.limits.maxTitle} aria-label="Envelope title" className="min-w-0 flex-1 bg-transparent text-base font-semibold text-ink rounded-md px-2 h-9 hover:bg-surface-2 focus:bg-surface-2" />
        <span className={cx("text-xs whitespace-nowrap hidden sm:inline", saveState === "error" ? "text-bad" : "text-muted")} role="status">
          {saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved" : saveState === "error" ? "Not saved" : "Saved"}
        </span>
        <Button variant="ghost" onClick={() => setDeleting(true)} aria-label="Delete draft" title="Delete draft">
          <Trash2 className="size-4" />
        </Button>
        <Button variant="primary" onClick={() => void save().then(() => setSending(true), () => setSending(true))} disabled={!pages.length}>
          <Send className="size-4" /> Review and send
        </Button>
      </div>
      {saveError && (
        <div className="px-4 pt-3">
          <ErrorNote>{saveError}</ErrorNote>
        </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* recipients + palette */}
        <aside className="lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-line bg-surface p-4 flex flex-col gap-5 lg:overflow-y-auto lg:h-[calc(100vh-3.5rem)] lg:sticky lg:top-14">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Recipients</h2>
              <div className="flex gap-1 rounded-lg bg-surface-2 p-0.5" role="radiogroup" aria-label="Signing order">
                {(["sequential", "parallel"] as const).map((r) => (
                  <button key={r} role="radio" aria-checked={draft.routing === r} onClick={() => update((d) => ({ ...d, routing: r }))} className={cx("px-2 h-7 rounded-md text-xs font-medium", draft.routing === r ? "bg-surface shadow-sm text-ink" : "text-muted")}>
                    {r === "sequential" ? "In order" : "All at once"}
                  </button>
                ))}
              </div>
            </div>
            <ul className="flex flex-col gap-2">
              {[...draft.recipients]
                .sort((a, b) => a.order - b.order)
                .map((r, i, all) => (
                  <li key={r.rid} className={cx("rounded-lg border p-2.5", active === r.rid ? "border-accent bg-accent-soft" : "border-line")} onClick={() => r.role === "signer" && setActive(r.rid)}>
                    <div className="flex items-center gap-2">
                      <span className="size-3 rounded-full shrink-0" style={{ background: rColor(r.color) }} aria-hidden />
                      {draft.routing === "sequential" && <span className="text-xs text-muted tabular w-4">{i + 1}</span>}
                      <select value={r.role} disabled={r.isSender} onChange={(e) => setRecipient(r.rid, { role: e.target.value as Recipient["role"] })} className="h-7 rounded-md border border-line bg-surface text-xs px-1" aria-label="Role">
                        <option value="signer">Signs</option>
                        <option value="cc">Gets a copy</option>
                      </select>
                      {r.isSender && <span className="text-xs font-medium text-accent-text">Me</span>}
                      <div className="ml-auto flex">
                        {draft.routing === "sequential" && (
                          <>
                            <button className="p-1 rounded text-muted hover:bg-surface-2 disabled:opacity-30" disabled={i === 0} onClick={(e) => (e.stopPropagation(), move(r.rid, -1))} aria-label="Move up">
                              <ArrowUp className="size-3.5" />
                            </button>
                            <button className="p-1 rounded text-muted hover:bg-surface-2 disabled:opacity-30" disabled={i === all.length - 1} onClick={(e) => (e.stopPropagation(), move(r.rid, 1))} aria-label="Move down">
                              <ArrowDown className="size-3.5" />
                            </button>
                          </>
                        )}
                        <button className="p-1 rounded text-muted hover:bg-surface-2" onClick={(e) => (e.stopPropagation(), removeRecipient(r.rid))} aria-label={`Remove ${r.name || "recipient"}`}>
                          <X className="size-3.5" />
                        </button>
                      </div>
                    </div>
                    <input value={r.name} readOnly={r.isSender} onChange={(e) => setRecipient(r.rid, { name: e.target.value })} placeholder="Full name" aria-label="Full name" className={cx(inputCls, "mt-2 h-8")} />
                    <input value={r.email} readOnly={r.isSender} type="email" onChange={(e) => setRecipient(r.rid, { email: e.target.value.trim() })} placeholder="Email" aria-label="Email" className={cx(inputCls, "mt-1.5 h-8")} />
                    {r.role === "signer" && <div className="text-xs text-muted mt-1.5">{draft.fields.filter((f) => f.rid === r.rid).length} field(s)</div>}
                  </li>
                ))}
            </ul>
            <div className="flex gap-2 mt-2">
              <Button className="flex-1" onClick={() => addRecipient()}>
                <UserPlus className="size-4" /> Add recipient
              </Button>
              {!draft.recipients.some((r) => r.isSender) && (
                <Button onClick={() => addRecipient(true)} title="Add yourself as a signer">
                  Add me
                </Button>
              )}
            </div>
            {draft.routing === "sequential" && draft.recipients.length > 1 && <p className="text-xs text-muted mt-2">Each person gets their email after the one above has signed.</p>}
          </section>

          {pages.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-1">Fields</h2>
              {activeR?.role === "signer" ? (
                <p className="text-xs text-muted mb-2">
                  Placing for <span className="font-medium" style={{ color: rColor(activeR.color) }}>{activeR.name || activeR.email || "this signer"}</span>. Pick a field, then click the page.
                </p>
              ) : (
                <p className="text-xs text-muted mb-2">Add a signer and select them to place their fields.</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {PALETTE.map(({ type, icon: Icon }) => (
                  <button
                    key={type}
                    disabled={activeR?.role !== "signer"}
                    onClick={() => setPlacing(placing === type ? null : type)}
                    aria-pressed={placing === type}
                    className={cx("flex items-center gap-2 h-9 px-2.5 rounded-lg border text-sm text-left disabled:opacity-40", placing === type ? "border-accent bg-accent-soft text-accent-text" : "border-line hover:bg-surface-2")}
                  >
                    <Icon className="size-4 shrink-0" /> {FIELD_LABEL[type]}
                  </button>
                ))}
              </div>
              {placing && <p className="text-xs text-accent-text mt-2">Click where the {FIELD_LABEL[placing].toLowerCase()} goes. Esc to cancel.</p>}
            </section>
          )}

          <RecordPicker envelopeId={env.id} link={view.envelope.link} onBeforeChange={() => save().catch(() => undefined)} onChange={(v) => setView((old) => ({ ...old, envelope: { ...old.envelope, link: v.envelope.link } }))} />

          {sel && (
            <section className="rounded-lg border border-line p-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold">{FIELD_LABEL[sel.type]}</h2>
                <span className="text-xs text-muted">page {sel.page + 1}</span>
              </div>
              <label className="flex items-center gap-2 text-sm mb-2">
                <span className="text-muted w-14">Signer</span>
                <select value={sel.rid} onChange={(e) => setField(sel.fid, { rid: e.target.value })} className={cx(inputCls, "h-8")}>
                  {signers.map((r) => (
                    <option key={r.rid} value={r.rid}>
                      {r.name || r.email || "Unnamed signer"}
                    </option>
                  ))}
                </select>
              </label>
              {(sel.type === "text" || sel.type === "checkbox") && (
                <>
                  <label className="flex items-center gap-2 text-sm mb-2">
                    <span className="text-muted w-14">Label</span>
                    <input value={sel.label ?? ""} maxLength={80} onChange={(e) => setField(sel.fid, { label: e.target.value })} placeholder={sel.type === "text" ? "e.g. Job title" : "e.g. I accept"} className={cx(inputCls, "h-8")} />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={sel.required} onChange={(e) => setField(sel.fid, { required: e.target.checked })} /> Required
                  </label>
                </>
              )}
              <div className="flex gap-2 mt-3">
                {pages.length > 1 && (
                  <Button className="h-8 flex-1" onClick={() => copyToAllPages(sel.fid)}>
                    <Copy className="size-3.5" /> All pages
                  </Button>
                )}
                <Button className="h-8" variant="ghost" onClick={() => removeField(sel.fid)}>
                  <Trash2 className="size-3.5" /> Remove
                </Button>
              </div>
            </section>
          )}
        </aside>

        {/* document */}
        <div className={cx("flex-1 min-w-0 bg-surface-2 p-4 lg:p-8", placing && "cursor-crosshair")}>
          {uploading ? (
            <div className="max-w-md mx-auto mt-16 bg-surface border border-line rounded-xl p-8 text-center">
              <Loader2 className="size-8 mx-auto animate-spin text-accent" />
              <p className="mt-3 text-sm">Uploading and checking {uploading}…</p>
            </div>
          ) : !pages.length || !view.docUrl ? (
            <DropZone onFile={upload} onPick={() => fileInput.current?.click()} maxBytes={me.limits.maxPdfBytes} maxPages={me.limits.maxPages} />
          ) : (
            <>
              <div className="max-w-[860px] mx-auto mb-3 flex items-center justify-between text-xs text-muted">
                <span className="truncate">
                  {view.envelope.fileName} · {pages.length} page{pages.length === 1 ? "" : "s"}
                </span>
                <button className="inline-flex items-center gap-1 hover:text-ink" onClick={() => fileInput.current?.click()}>
                  <FileUp className="size-3.5" /> Replace PDF
                </button>
              </div>
              <PdfPages
                url={view.docUrl}
                pages={pages}
                onPageClick={(p, fx, fy) => (placing ? place(p, fx, fy) : setSelected(null))}
                overlay={(p) => (fieldsByPage.get(p) ?? []).map((f) => <FieldBox key={f.fid} f={f} color={rColor(byRid.get(f.rid)?.color ?? 0)} who={byRid.get(f.rid)?.name} selected={selected === f.fid} onSelect={() => (setSelected(f.fid), setActive(f.rid))} onChange={(patch) => setField(f.fid, patch)} />)}
              />
            </>
          )}
          <input ref={fileInput} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => (e.target.files?.[0] && upload(e.target.files[0]), (e.target.value = ""))} />
        </div>
      </div>

      {sending && (
        <SendDialog
          draft={draft}
          problems={view.problems}
          link={view.envelope.link}
          onChange={(patch) => update((d) => ({ ...d, ...patch }))}
          onClose={() => setSending(false)}
          onSend={async () => {
            await save();
            const v = await api<EnvelopeView>(`/envelopes/${env.id}/send`, { body: { confirmSuitable: true } });
            toast("Sent. Signers will get their email in a moment.");
            onSent(v);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title="Delete this draft?"
          confirm="Delete draft"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api(`/envelopes/${env.id}`, { method: "DELETE" });
            nav("/");
          }}
        >
          <p>The uploaded PDF, recipients and fields are deleted. Nothing was sent.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const renumber = (rs: Recipient[]) => [...rs].sort((a, b) => a.order - b.order).map((r, i) => ({ ...r, order: i + 1 }));

function DropZone({ onFile, onPick, maxBytes, maxPages }: { onFile: (f: File) => void; onPick: () => void; maxBytes: number; maxPages: number }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={cx("max-w-xl mx-auto mt-10 rounded-2xl border-2 border-dashed p-10 text-center bg-surface", over ? "border-accent bg-accent-soft" : "border-line")}
    >
      <FileUp className="size-10 mx-auto text-muted" />
      <h2 className="mt-3 font-semibold">Upload the document to sign</h2>
      <p className="text-sm text-muted mt-1">
        Drop a PDF here, or choose one. Up to {bytes(maxBytes)} and {maxPages} pages.
      </p>
      <Button variant="primary" className="mt-5" onClick={onPick}>
        Choose PDF
      </Button>
      <p className="text-xs text-muted mt-4">Word document? Save it as PDF first (File → Save As → PDF).</p>
    </div>
  );
}

function FieldBox({ f, color, who, selected, onSelect, onChange }: { f: Field; color: string; who?: string; selected: boolean; onSelect: () => void; onChange: (p: Partial<Field>) => void }) {
  const drag = (e: RPointerEvent, mode: "move" | "resize") => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    const page = (e.currentTarget as HTMLElement).closest("[data-page]") as HTMLElement;
    const rect = page.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, f: { ...f } };
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - start.x) / rect.width;
      const dy = (ev.clientY - start.y) / rect.height;
      if (mode === "move") onChange({ x: clamp(start.f.x + dx, 0, 1 - start.f.w), y: clamp(start.f.y + dy, 0, 1 - start.f.h) });
      else onChange({ w: clamp(start.f.w + dx, 0.015, 1 - start.f.x), h: clamp(start.f.h + dy, 0.01, 1 - start.f.y) });
    };
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${FIELD_LABEL[f.type]} for ${who || "signer"}`}
      onPointerDown={(e) => drag(e, "move")}
      onFocus={onSelect}
      className={cx("absolute flex items-center justify-center rounded-[3px] text-[11px] font-medium cursor-move overflow-hidden touch-none", selected && "ring-2 ring-offset-1 ring-offset-white")}
      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, border: `2px solid ${color}`, background: `color-mix(in srgb, ${color} 14%, white)`, color, ["--tw-ring-color" as any]: color }}
    >
      <span className="truncate px-1 pointer-events-none">{f.type === "checkbox" ? "" : FIELD_LABEL[f.type]}</span>
      {f.required && f.type !== "checkbox" && <span className="absolute top-0 right-0.5 leading-none pointer-events-none">*</span>}
      {selected && <span onPointerDown={(e) => drag(e, "resize")} className="absolute right-0 bottom-0 size-3 cursor-se-resize" style={{ background: color }} aria-hidden />}
    </div>
  );
}

function SendDialog({ draft, problems, link, onChange, onClose, onSend }: { draft: Draft; problems: string[]; link?: RecordLink; onChange: (p: Partial<Draft>) => void; onClose: () => void; onSend: () => Promise<void> }) {
  const me = useMe();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signers = draft.recipients.filter((r) => r.role === "signer");
  const ccs = draft.recipients.filter((r) => r.role === "cc");
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSend();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Review and send"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Back to editing</Button>
          <Button variant="primary" busy={busy} disabled={!confirm || problems.length > 0} onClick={send}>
            <Send className="size-4" /> Send
          </Button>
        </>
      }
    >
      {problems.length > 0 && (
        <div className="mb-4">
          <ErrorNote>
            <div className="font-medium mb-1">Before sending:</div>
            <ul className="list-disc pl-4">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </ErrorNote>
        </div>
      )}
      <div className="text-sm text-ink-2 mb-4">
        <div>
          <span className="text-muted">Signers: </span>
          {signers.map((r, i) => (
            <span key={r.rid}>
              {i > 0 && (draft.routing === "sequential" ? " → " : ", ")}
              {r.name || r.email}
            </span>
          ))}
        </div>
        {link && (
          <div>
            <span className="text-muted">Record: </span>
            {link.recordName} (signed PDF filed there when complete)
          </div>
        )}
        {ccs.length > 0 && (
          <div>
            <span className="text-muted">Copies to: </span>
            {ccs.map((r) => r.name || r.email).join(", ")}
          </div>
        )}
      </div>
      <label className="block text-sm font-medium text-ink-2 mb-1" htmlFor="send-title">
        Email subject (document title)
      </label>
      <input id="send-title" value={draft.title} maxLength={me.limits.maxTitle} onChange={(e) => onChange({ title: e.target.value })} className={inputCls} />
      <label className="block text-sm font-medium text-ink-2 mb-1 mt-3" htmlFor="send-msg">
        Message to signers <span className="text-muted font-normal">(optional)</span>
      </label>
      <textarea id="send-msg" value={draft.message} maxLength={me.limits.maxMessage} onChange={(e) => onChange({ message: e.target.value })} rows={3} className={cx(inputCls, "h-auto py-2")} />
      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="text-sm">
          <span className="block font-medium text-ink-2 mb-1">Expires after (days)</span>
          <input type="number" min={me.limits.minExpiryDays} max={me.limits.maxExpiryDays} value={draft.expiryDays} onChange={(e) => onChange({ expiryDays: Number(e.target.value) })} className={inputCls} />
        </label>
        <label className="text-sm">
          <span className="block font-medium text-ink-2 mb-1">Remind every (days, 0 = off)</span>
          <input type="number" min={0} max={me.limits.maxReminderDays} value={draft.reminderDays} onChange={(e) => onChange({ reminderDays: Number(e.target.value) })} className={inputCls} />
        </label>
      </div>
      <div className="mt-4 rounded-lg bg-warn-soft text-ink-2 p-3 text-xs leading-relaxed">
        Electronic signatures aren't valid for every document. Don't use eSignIt for wills, codicils or testamentary trusts; court orders or court filings; notarised documents; adoption, divorce or other family-law documents; real-estate documents that must be recorded on paper; or notices of utility shut-off, foreclosure, eviction, insurance cancellation or product recalls. If you're unsure, ask your legal adviser first.
      </div>
      <label className="flex items-start gap-2 mt-3 text-sm">
        <input type="checkbox" className="mt-0.5" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
        <span>I confirm this document is suitable for electronic signature.</span>
      </label>
      {error && <p className="text-sm text-bad mt-3">{error}</p>}
    </Modal>
  );
}
